import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useErrorToast } from "@/lib/hooks";
import { useI18n } from "@/i18n/I18n";

const get = (url) => () => api.get(url).then((r) => r.data);

const AssignDialog = ({ ws, plans, onClose }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const showErr = useErrorToast();
  const [planId, setPlanId] = useState(ws.plan_id || "free");
  const [exp, setExp] = useState(ws.plan_expires_at?.slice(0, 10) || "");
  const [days, setDays] = useState(7);
  const done = () => { qc.invalidateQueries({ queryKey: ["admin-ws"] }); toast.success(t("common.saved")); onClose(); };
  const assign = async () => {
    try { await api.put(`/admin/workspaces/${ws.id}/plan`, { plan_id: planId, expires_at: exp ? new Date(exp + "T23:59:59Z").toISOString() : null }); done(); } catch (e) { showErr(e); }
  };
  const extend = async () => { try { await api.post(`/admin/workspaces/${ws.id}/trial`, { days: Number(days) }); done(); } catch (e) { showErr(e); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="admin-assign-dialog">
        <DialogHeader><DialogTitle>{ws.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Label>{t("admin.plan")}</Label>
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger data-testid="admin-plan-select" className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{plans.filter((p) => p.id !== "trial").map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Label>{t("admin.expiresAt")}</Label>
          <Input data-testid="admin-expires-input" type="date" className="h-11" value={exp} onChange={(e) => setExp(e.target.value)} />
          <Button data-testid="admin-assign-button" className="w-full h-11" onClick={assign}>{t("admin.assign")}</Button>
        </div>
        <DialogFooter className="flex-col sm:flex-col gap-2 border-t pt-4">
          <Label>{t("admin.extendTrial")}</Label>
          <div className="flex gap-2">
            <Input data-testid="admin-trial-days" type="number" min={0} className="h-11" value={days} onChange={(e) => setDays(e.target.value)} />
            <Button data-testid="admin-extend-button" variant="outline" className="h-11" onClick={extend}>{t("admin.extend")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WorkspacesTab = ({ plans }) => {
  const { t, fmtDate, fmtNum } = useI18n();
  const [sel, setSel] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["admin-ws"], queryFn: get("/admin/workspaces") });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
      <Table data-testid="admin-ws-table">
        <TableHeader><TableRow>
          <TableHead>{t("set.workspace")}</TableHead><TableHead>{t("admin.owner")}</TableHead><TableHead>{t("admin.plan")}</TableHead>
          <TableHead>{t("admin.trialEnd")}</TableHead><TableHead>{t("admin.usage")}</TableHead><TableHead />
        </TableRow></TableHeader>
        <TableBody>
          {data.map((w) => (
            <TableRow key={w.id} data-testid={`admin-ws-row-${w.id}`}>
              <TableCell><p className="font-semibold">{w.name}</p><p className="text-xs text-slate-500">{t(w.type === "business" ? "onb.business" : "onb.personal")}</p></TableCell>
              <TableCell className="text-sm">{w.owner?.email}</TableCell>
              <TableCell><p className="font-medium">{plans.find((p) => p.id === w.effective_plan)?.name}</p><p className="text-xs text-slate-500">{t(`status.${w.status}`)}{w.plan_expires_at ? ` · ${fmtDate(w.plan_expires_at)}` : ""}</p></TableCell>
              <TableCell className="text-sm">{fmtDate(w.trial_ends_at)}</TableCell>
              <TableCell className="font-mono text-xs whitespace-nowrap">{fmtNum(w.usage.items)} obj · {w.usage.places} lieux · {w.usage.members} mbr · {w.usage.ai} IA</TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => setSel(w)} data-testid={`admin-ws-manage-${w.id}`}>{t("admin.assign")}</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {sel && <AssignDialog ws={sel} plans={plans} onClose={() => setSel(null)} />}
    </div>
  );
};

const PlanEditor = ({ plan }) => {
  const { t } = useI18n();
  const qc = useQueryClient();
  const showErr = useErrorToast();
  const [p, setP] = useState(plan);
  useEffect(() => setP(plan), [plan]);
  const lim = (k) => (e) => setP({ ...p, limits: { ...p.limits, [k]: Number(e.target.value) } });
  const save = async () => {
    try {
      await api.put(`/admin/plans/${p.id}`, { name: p.name, price_chf: p.price_chf === "" || p.price_chf == null ? null : Number(p.price_chf), quote: p.quote, price_eur: p.price_eur === "" || p.price_eur == null ? null : Number(p.price_eur), yearly_discount: Number(p.yearly_discount) || 0, limits: p.limits, features: p.features });
      qc.invalidateQueries({ queryKey: ["admin-plans"] });
      toast.success(t("common.saved"));
    } catch (e) { showErr(e); }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4" data-testid={`admin-plan-${p.id}`}>
      <div className="flex items-center justify-between gap-2"><span className="font-mono text-xs text-slate-400">{p.id}</span></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2 space-y-1"><Label>{t("common.name")}</Label><Input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} /></div>
        <div className="space-y-1"><Label>{t("admin.price")}</Label><Input data-testid={`admin-plan-price-${p.id}`} type="number" step="0.1" value={p.price_chf ?? ""} onChange={(e) => setP({ ...p, price_chf: e.target.value })} /></div>
        <div className="space-y-1"><Label>{t("admin.priceEur")}</Label><Input data-testid={`admin-plan-price-eur-${p.id}`} type="number" step="0.1" value={p.price_eur ?? ""} onChange={(e) => setP({ ...p, price_eur: e.target.value })} /></div>
        <div className="space-y-1"><Label>{t("admin.yearlyDiscount")}</Label><Input data-testid={`admin-plan-discount-${p.id}`} type="number" min={0} max={90} value={p.yearly_discount ?? 0} onChange={(e) => setP({ ...p, yearly_discount: e.target.value })} /></div>
        {["items", "places", "members", "ai"].map((k) => (
          <div key={k} className="space-y-1"><Label className="capitalize">{k} (-1 = ∞)</Label><Input data-testid={`admin-plan-${k}-${p.id}`} type="number" value={p.limits[k]} onChange={lim(k)} /></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-4">
        {["qr", "ai", "history"].map((f) => (
          <label key={f} className="flex items-center gap-2 text-sm"><Switch checked={!!p.features[f]} onCheckedChange={(v) => setP({ ...p, features: { ...p.features, [f]: v } })} />{f}</label>
        ))}
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!p.quote} onCheckedChange={(v) => setP({ ...p, quote: v })} />{t("plan.quote")}</label>
      </div>
      <Button data-testid={`admin-plan-save-${p.id}`} onClick={save} className="w-full">{t("common.save")}</Button>
    </div>
  );
};

const Interests = () => {
  const { fmtDateTime } = useI18n();
  const { data = [] } = useQuery({ queryKey: ["admin-int"], queryFn: get("/admin/interests") });
  return (
    <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="admin-interests">
      {data.map((i) => <li key={i.id} className="p-4 text-sm flex flex-wrap justify-between gap-2"><span><b>{i.workspace_name}</b> · {i.email}</span><span className="font-mono">{i.plan_id} · {fmtDateTime(i.created_at)}</span></li>)}
      {!data.length && <li className="p-6 text-center text-slate-400">—</li>}
    </ul>
  );
};

export default function Admin() {
  const { t } = useI18n();
  const { data: plans = [] } = useQuery({ queryKey: ["admin-plans"], queryFn: get("/admin/plans") });
  return (
    <div>
      <PageHeader title={t("admin.title")} testId="admin-title" />
      <Tabs defaultValue="ws">
        <TabsList className="mb-6 h-11">
          <TabsTrigger value="ws" data-testid="admin-tab-ws">{t("admin.workspaces")}</TabsTrigger>
          <TabsTrigger value="plans" data-testid="admin-tab-plans">{t("admin.plans")}</TabsTrigger>
          <TabsTrigger value="int" data-testid="admin-tab-interests">{t("admin.interests")}</TabsTrigger>
        </TabsList>
        <TabsContent value="ws"><WorkspacesTab plans={plans} /></TabsContent>
        <TabsContent value="plans"><div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">{plans.map((p) => <PlanEditor key={p.id} plan={p} />)}</div></TabsContent>
        <TabsContent value="int"><Interests /></TabsContent>
      </Tabs>
    </div>
  );
}
